import { Stack, PrimaryButton, IButton, IContextualMenuProps, Label, TooltipHost, DirectionalHint } from "@fluentui/react";
import { useRefEffect } from "@fluentui/react-hooks";
import React, { useEffect, useState } from "react";
import { FieldProps, getUiOptions } from "@rjsf/utils";
import { IOAuthAccount } from "../../models/IOAuthAccount";
import { getLocalizedString } from "../../components/testinput/LocalizedText";
import { ErrorListField } from "../../components/testinput/ErrorListField";
import { useAppDispatch, useAppSelector, useSelectStyles } from "../../hooks";
import { refreshOAuthAccounts, acquireTokenInteractive, logoutAccount } from "./oauthSlice";

/**
 * Builds OAuth token from validation errors or from error state values
 *
 * @param rawErrors - The raw errors
 * @param accountsLoadError - the accounts load error
 * @param acquireTokenError - the acquire token error
 * @returns the array of errors
 */
const buildOAuthErrors = (rawErrors: string[], accountsLoadError: string, acquireTokenError: string): string[] => {
  const errors = [];
  acquireTokenError ? errors.push(acquireTokenError) : null;
  accountsLoadError ? errors.push(accountsLoadError) : null;

  return errors.concat(rawErrors);
};

/**
 * A Field to collect or select credentials for OAuth authentication
 *
 * @param props - the component props
 * @returns the markup
 */
export const OAuthField: React.FC<FieldProps<IOAuthAccount>> = (props) => {
  const {
    formData,
    idSchema,
    onChange,
    required,
    schema: { title, description },
    uiSchema,
  } = props;

  const rawErrors = props.rawErrors as string[];

  const dispatch = useAppDispatch();
  const {
    accountList: accounts,
    accountsLoading,
    accountsLoadError,
    loginOrLogoutError: acquireTokenError,
    loginOrLogoutInProgress,
  } = useAppSelector((s) => s.oauth);
  const userNameHint = useAppSelector((s) => s.testInput.userNameHint);

  const uiOptions = getUiOptions(uiSchema);

  const dropdownRef = useRefEffect<HTMLSelectElement>((select) => {
    if (uiOptions && uiOptions.autofocus) {
      select.focus();
    }
  });
  const primaryButtonRef = React.useRef<IButton | null>(null);
  const previousAcquireTokenErrorRef = React.useRef(acquireTokenError);

  useEffect(() => {
    void dispatch(refreshOAuthAccounts());
  }, []);

  useEffect(() => {
    onChange({ homeAccountId: "", username: "", environment: "", tenantId: "" });
  }, []);

  useEffect(() => {
    const hadError = Boolean(previousAcquireTokenErrorRef.current);
    const hasError = Boolean(acquireTokenError);

    if (!hadError && hasError) {
      primaryButtonRef.current?.focus();
    }

    previousAcquireTokenErrorRef.current = acquireTokenError;
  }, [acquireTokenError]);

  const [lastAutoSelectedByUserNameHint, setLastAutoSelectedByUserNameHint] = useState<string | null>(null);

  const selectStyles = useSelectStyles({ width: "100%" });

  // This effect handles auto-selecting an account based on the username hint
  useEffect(() => {
    // If the username hint has changed since last time, try auto-selecting by the hint
    if (userNameHint && lastAutoSelectedByUserNameHint !== userNameHint) {
      const autoSelected = accounts?.filter((a) => a.username.toUpperCase() === userNameHint)[0];

      if (autoSelected) {
        setLastAutoSelectedByUserNameHint(userNameHint.toUpperCase());
        onChange(autoSelected);
      }
    }
  }, [accounts, userNameHint]);

  // This effect handles auto-selecting a default account if none is selected
  useEffect(() => {
    if (!formData?.homeAccountId && accounts?.length) {
      onChange(accounts[0]);
    }
  }, [accounts, formData?.homeAccountId]);

  if (accountsLoading) {
    return <div>{ClientStrings.OAuthCredentialsLoadingPlaceholder}</div>;
  }

  const options = accounts.map((acct) => ({
    key: acct.homeAccountId,
    text: acct.username,
  }));

  const errors = buildOAuthErrors(rawErrors, accountsLoadError, acquireTokenError);

  /**
   * Handles when a selected OAuth account changes
   *
   * @param ev - the event
   */
  const handleChange = (ev: React.ChangeEvent<HTMLSelectElement>): void => {
    const account = accounts.filter((a) => a.homeAccountId === ev.target.value)[0];
    const oAuthAccount = account ?? undefined;

    onChange(oAuthAccount);
  };

  const descriptionId = description ? idSchema.$id + "__description" : "";
  const helpId = uiOptions?.help ? idSchema.$id + "__help" : "";
  const errorId = errors.length ? idSchema.$id + "__error" : "";
  const acquireTokenErrorId = acquireTokenError ? idSchema.$id + "__acquireTokenError" : "";
  const selectAriaDescribedBy = [errorId, descriptionId, helpId].filter(Boolean).join(" ") || undefined;
  const buttonAriaDescribedBy = acquireTokenError ? acquireTokenErrorId : undefined;

  const isLoggedIn = options.length > 0;
  let primaryButtonText: string;
  let primaryButtonAction: () => Promise<void>;
  if (isLoggedIn) {
    primaryButtonText = ClientStrings.OAuthCredentialsButtonTextSignOut;
    primaryButtonAction = async () => {
      await dispatch(logoutAccount(formData?.homeAccountId ?? ""));
    };
  } else {
    primaryButtonText = ClientStrings.OAuthCredentialsButtonText;
    primaryButtonAction = () => dispatch(acquireTokenInteractive());
  }

  const menuProps: IContextualMenuProps | undefined = isLoggedIn
    ? {
        items: [
          {
            key: "addAccount",
            text: ClientStrings.OAuthCredentialsButtonTextAdd,
            iconProps: { iconName: "Permissions" },
            onClick: () => void dispatch(acquireTokenInteractive()),
          },
        ],
      }
    : undefined;

  const tooltipText = options.length && formData?.username ? formData?.username : ClientStrings.OAuthCredentialsEmptyPlaceholder;

  return (
    <Stack>
      {title && (
        <Label htmlFor={idSchema.$id}>
          {getLocalizedString(title)}
          {required && (
            <span className="required-field-indicator" aria-hidden="true">
              {" "}
              *
            </span>
          )}
        </Label>
      )}
      <Stack
        horizontal
        verticalAlign="start"
        className="rca-Custom-field"
        styles={{ root: { alignItems: "flex-end" } }}
        tokens={{ childrenGap: 8 }}
      >
        <TooltipHost content={tooltipText} directionalHint={DirectionalHint.bottomCenter}>
          <select
            ref={dropdownRef}
            className={selectStyles}
            id={idSchema.$id}
            required={required}
            value={formData?.homeAccountId}
            onChange={handleChange}
            onBlur={handleChange}
            aria-describedby={selectAriaDescribedBy}
          >
            {options.length ? (
              options.map((o) => {
                const isSelected = o.key === formData?.homeAccountId;
                return (
                  <option aria-selected={isSelected} key={o.key} value={o.key}>
                    {o.text}
                  </option>
                );
              })
            ) : (
              <option value="" aria-selected="true">
                {ClientStrings.OAuthCredentialsEmptyPlaceholder}
              </option>
            )}
          </select>
        </TooltipHost>
        <PrimaryButton
          componentRef={primaryButtonRef}
          split={isLoggedIn}
          menuProps={menuProps}
          iconProps={{ iconName: "Permissions" }}
          styles={{ textContainer: { whiteSpace: "noWrap" } }}
          text={primaryButtonText}
          disabled={loginOrLogoutInProgress}
          aria-describedby={buttonAriaDescribedBy}
          onClick={() => void primaryButtonAction()}
        />
      </Stack>
      {acquireTokenError ? (
        <span
          id={acquireTokenErrorId}
          style={{
            border: 0,
            clip: "rect(0 0 0 0)",
            height: 1,
            margin: -1,
            overflow: "hidden",
            padding: 0,
            position: "absolute",
            width: 1,
          }}
        >
          {acquireTokenError}
        </span>
      ) : undefined}
      {errors.length ? <ErrorListField id={`${idSchema.$id}__error`} rawErrors={errors} /> : undefined}
    </Stack>
  );
};
